/**
 * Copyright (c) 2017-present, Tomaruba, Inc.
 * All rights reserved.
 */
import React from 'react'
import nl2br from 'react-nl2br'
import r from '../../../util/Renderer'
import { dateToString, dateToStringDate } from '../../../util/DateString'
import { calcStayingDays } from '../../../util/DateProcessing'
import AccommodationTax from '../../organisms/tax/accommodation/AccommodationTax'
import ReservationAction from '../../../action/reservation'
import AccommodationTaxAction from '../../../action/accommodation_tax'
import ReservationTab from './detail/ReservationTab'
import ReservationLog from './detail/ReservationLog'
import AutoActionLog from './detail/AutoActionLog'
import Money from '../../unit/Money'
import Invoices from './detail/Invoices'
import MaterialIcon from '@material/react-material-icon'
import { Status, NoShow } from '../../parts/Reservation'
import { PaymentPaid } from '../../parts/Payment'
import {
  Detail,
  Dialog,
  DialogButton,
  DialogContent,
  DialogFooter,
  MdcList,
  MdcListDivider,
  MdcListGroup,
  MdcListItem,
  MdcListItemGraphic,
  MdcListItemMeta,
  MdcListItemText,
  TabHeader
} from 'ui-library/dist/external/components'

export default function ReservationDetail(props) {
  const page = props.page
  const { reservation, tasks, audits, session, invoices } = props.children
  const [showFooter, toggleFooter] = React.useState(!session.isOwner)
  const tab = props.children.match.params.tab || props.children.tab
  const tabs = ReservationTab.getTabMenus(props.base, reservation.id, session.isOwner)

  const paid_fee = reservation.is_paid_accommodation_fee
  const paid_tax = reservation.is_paid_accommodation_tax
  const menuFeeLabel = reservation.is_paid_accommodation_fee ? '宿泊料金を未受領にする' : '宿泊料金を受領済みにする'
  const menuTaxLabel = reservation.is_paid_accommodation_tax ? '宿泊税を未受領にする' : '宿泊税を受領済みにする'
  const noshowLabel = reservation.noshow ? 'ノーショーを解除' : 'ノーショーにする'
  return (
    <Detail
      showHeader
      showFooter={showFooter}
      onDeactivate={ReservationAction.hideDetailView}
      primaryButton={{
        label: '　宿泊者名簿　',
        onClick: e => ReservationAction.toCheckInView(e, reservation.id)
      }}
      secondaryButton={{
        label: '　　編集　　',
        onClick: e => ReservationAction.toEditView(reservation.id)
      }}
      menuOptions={[
        {
          label: '客層登録',
          onClick: e => {
            ReservationAction.toSegmentView(reservation.id)
          }
        },
        { label: '請求書発行 (宿泊費・宿泊税)', onClick: e => ReservationAction.toCreateInvoiceView(reservation.id) },
        { label: menuFeeLabel, onClick: e => ReservationAction.onUpdateReservationPay(reservation.id, !paid_fee, paid_tax) },
        { label: menuTaxLabel, onClick: e => ReservationAction.onUpdateReservationPay(reservation.id, paid_fee, !paid_tax) },
        { label: noshowLabel, onClick: e => ReservationAction.onUpdateNoshow(reservation.id, !reservation.noshow) },
        { label: '予約取消', onClick: e => ReservationAction.onRequestCancel(reservation) }
      ]}
    >
      <TabHeader
        onClose={ReservationAction.hideDetailView}
        activeIndex={getActiveIndex(tab)}
        tabs={Tabs(tab, session.isOwner)}
        handleActiveIndexUpdate={index => handleActiveIndexUpdate(index, page, reservation.id)}
      />
      <DetailContent tab={tab} reservation={reservation} accommodation_tax={reservation.accommodation_tax} tasks={tasks} session={session} toggleFooter={toggleFooter} />
      {r.renderIf(!session.isOwner)(<Invoices tab={tab} invoices={invoices} />)}
      {r.renderIf(!session.isOwner)(<ReservationLog tab={tab} audits={props.children.audits} />)}
      {r.renderIf(!session.isOwner)(<AutoActionLog tab={tab} auto_action_logs={props.children.auto_action_logs} />)}
    </Detail>
  )
}

function getActiveIndex(tab) {
  switch (tab) {
    case 'detail':
      return 0
    case 'invoices':
      return 1
    case 'log':
      return 2
    case 'auto_action_log':
      return 3
    default:
      return 0
  }
}

function Tabs(tab, isOwner) {
  if (isOwner) return ['予約詳細']

  return ['予約詳細', '請求書', '変更履歴', '自動アクション']
}

function handleActiveIndexUpdate(index, page, reservationId) {
  switch (index) {
    case 0:
      ReservationAction.toDetailTabView(page, reservationId)
      break
    case 1:
      ReservationAction.toInvoiceTabView(page, reservationId)
      break
    case 2:
      ReservationAction.toAuditLogTabView(page, reservationId)
      break
    case 3:
      ReservationAction.toAutoActionTabView(page, reservationId)
      break
    default:
      ReservationAction.toDetailTabView(page, reservationId)
      break
  }
}

/**
 * 予約詳細表示
 */
function DetailContent(props) {
  if (!props.tab || props.tab !== 'detail') {
    return null
  }

  const reservation = props.reservation
  const tax = props.accommodation_tax
  const accommodation_tax = tax ? Math.ceil(tax.tax_per_day * (tax.number_of_nights * tax.number_of_guests)) : null
  const onSave = tax && tax.id ? accom_tax => AccommodationTaxAction.onRequestUpdate(reservation.id, accom_tax) : accom_tax => AccommodationTaxAction.onRequestCreate(reservation.id, accom_tax)
  const room_link = `/setting/${reservation.room_id}/info`

  return (
    <MdcListGroup>
      <MdcList nonInteractive>
        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="book" />} />
          {r.renderIfOrElse(!reservation.noshow)(<MdcListItemText primaryText={<Status type={reservation.status}>{reservation.getStatusLabel()}</Status>} />)(
            <MdcListItemText primaryText={<Status type={reservation.status}>{reservation.getStatusLabel()}</Status>} secondaryText={<NoShow>ノーショー</NoShow>} />
          )}
          <MdcListItemMeta meta={<small>予約状況</small>} />
        </MdcListItem>

        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="date_range" />} />
          <MdcListItemText
            primaryText={`${dateToStringDate(reservation.check_in)} 〜 ${dateToStringDate(reservation.check_out)}`}
            secondaryText={`${calcStayingDays(reservation.check_in, reservation.check_out)}泊`}
          />
          <MdcListItemMeta meta={<small>滞在</small>} />
        </MdcListItem>

        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="location_city" />} />
          <MdcListItemText
            primaryText={
              <a href={room_link} target="_blank">
                {reservation.stay}
              </a>
            }
          />
          <MdcListItemMeta meta={<small>宿泊施設</small>} />
        </MdcListItem>

        {r.renderIf(reservation.plan_name)(
          <MdcListItem style={{ height: 'auto', minHeight: '48px' }}>
            <MdcListItemGraphic graphic={<MaterialIcon icon="menu_book" />} />
            <MdcListItemText className={`multiLine`} primaryText={reservation.plan_name} />
            <MdcListItemMeta meta={<small>プラン</small>} />
          </MdcListItem>
        )}

        {r.renderIf(reservation.getMealCondition())(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="set_meal" />} />
            <MdcListItemText primaryText={reservation.getMealCondition()} />
            <MdcListItemMeta meta={<small>食事条件</small>} />
          </MdcListItem>
        )}
        {r.renderIf(reservation.meal_delivery)(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="access_time" />} />
            <MdcListItemText primaryText={reservation.meal_delivery} />
            <MdcListItemMeta meta={<small>食事時刻</small>} />
          </MdcListItem>
        )}

        {r.renderIf(reservation.key_code)(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="vpn_key" />} />
            <MdcListItemText primaryText={reservation.key_code} />
            <MdcListItemMeta meta={<small>鍵番号</small>} />
          </MdcListItem>
        )}

        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="face" />} />
          <MdcListItemText primaryText={reservation.name} />
          <MdcListItemMeta meta={<small>予約名</small>} />
        </MdcListItem>

        {/* オーナーが予約情報を編集しないよう一時的な対応 */}
        {r.renderIf(!props.session.isOwner)(
          <MdcListItem style={{ height: 'auto', minHeight: '48px' }}>
            <MdcListItemGraphic graphic={<MaterialIcon icon="rate_review" />} />
            <MdcListItemText className={`multiLine`} primaryText={LargeOutput(reservation.notes)} />
            <MdcListItemMeta meta={<small>社内メモ</small>} />
          </MdcListItem>
        )}
        <MdcListItem style={{ height: 'auto', minHeight: '48px' }}>
          <MdcListItemGraphic graphic={<MaterialIcon icon="rate_review" />} />
          <MdcListItemText className={`multiLine`} primaryText={LargeOutput(reservation.shared_notes)} />
          <MdcListItemMeta meta={<small>社外共有</small>} />
        </MdcListItem>
      </MdcList>

      <MdcListDivider tag="div" />
      <ListTitle title={'予約詳細'} />
      <MdcList nonInteractive>
        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="money" />} />
          <MdcListItemText primaryText={<Money>{reservation.accommodation_fee}</Money>} />
          <MdcListItemMeta meta={<small>予約料金</small>} />
        </MdcListItem>

        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="check" />} />
          <MdcListItemText primaryText={<PaymentPaid paid={reservation.is_paid_accommodation_fee} />} />
          <MdcListItemMeta meta={<small>宿泊料金</small>} />
        </MdcListItem>

        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="people" />} />
          <MdcListItemText
            primaryText={
              <React.Fragment>
                <small>大人</small>
                &nbsp;<span style={{ fontSize: '1.1rem' }}>{reservation.num_adult}</span>
                <small>名</small>
              </React.Fragment>
            }
          />
          <MdcListItemMeta meta={<small>人数(大人)</small>} />
        </MdcListItem>

        {r.renderIf(reservation.num_child)(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="child_care" />} />
            <MdcListItemText
              primaryText={
                <React.Fragment>
                  <small>子供</small>
                  &nbsp;<span style={{ fontSize: '1.1rem' }}>{reservation.num_child}</span>
                  <small>名</small>
                </React.Fragment>
              }
            />
            <MdcListItemMeta meta={<small>人数(子供)</small>} />
          </MdcListItem>
        )}

        {r.renderIf(reservation.num_infant > 0)(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="child_friendly" />} />
            <MdcListItemText
              primaryText={
                <React.Fragment>
                  <small>幼児</small>
                  &nbsp;<span style={{ fontSize: '1.1rem' }}>{reservation.num_infant}</span>
                  <small>名</small>
                </React.Fragment>
              }
            />
            <MdcListItemMeta meta={<small>人数(幼児)</small>} />
          </MdcListItem>
        )}

        <MdcListItem style={{ height: 'auto', minHeight: '48px' }}>
          <MdcListItemGraphic graphic={<MaterialIcon icon="access_time" />} />
          <MdcListItemText primaryText={reservation.guest_arrival_time || NoData()} />
          <MdcListItemMeta meta={<small>到着予定</small>} />
        </MdcListItem>

        {r.renderIf(reservation.country)(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="outlined_flag" />} />
            <MdcListItemText primaryText={reservation.country} />
            <MdcListItemMeta meta={<small>国籍</small>} />
          </MdcListItem>
        )}

        {r.renderIf(reservation.phone_number)(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="local_phone" />} />
            <MdcListItemText primaryText={reservation.phone_number} />
            <MdcListItemMeta meta={<small>電話番号</small>} />
          </MdcListItem>
        )}

        {r.renderIf(reservation.mobile_number)(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="smartphone" />} />
            <MdcListItemText primaryText={reservation.mobile_number} />
            <MdcListItemMeta meta={<small>携帯番号</small>} />
          </MdcListItem>
        )}

        {r.renderIf(reservation.email)(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="mail_outline" />} />
            <MdcListItemText primaryText={reservation.email} />
            <MdcListItemMeta meta={<small>Email</small>} />
          </MdcListItem>
        )}
      </MdcList>

      {r.renderIf(!props.session.isOwner)(
        <>
          <MdcListDivider tag="div" />
          <ListTitle title={'宿泊税の徴収実績'} />

          {r.renderIf(tax)(
            <>
              <MdcList nonInteractive>
                <MdcListItem>
                  <MdcListItemGraphic graphic={<MaterialIcon icon="money" />} />
                  <MdcListItemText primaryText={<Money>{accommodation_tax}</Money>} />
                  <MdcListItemMeta meta={<small>税額合計</small>} />
                </MdcListItem>

                <MdcListItem>
                  <MdcListItemGraphic graphic={<MaterialIcon icon="date_range" />} />
                  <MdcListItemText primaryText={`${numberOf(tax, 'number_of_nights')}日`} />
                  <MdcListItemMeta meta={<small>滞在日数</small>} />
                </MdcListItem>

                <MdcListItem>
                  <MdcListItemGraphic graphic={<MaterialIcon icon="people" />} />
                  <MdcListItemText primaryText={`${numberOf(tax, 'number_of_guests')}人`} />
                  <MdcListItemMeta meta={<small>人数</small>} />
                </MdcListItem>

                <MdcListItem>
                  <MdcListItemGraphic graphic={<MaterialIcon icon="rate_review" />} />
                  <MdcListItemText className={`multiLine`} primaryText={LargeOutput(valueOf(tax, 'memo'))} />
                  <MdcListItemMeta meta={<small>メモ</small>} />
                </MdcListItem>
              </MdcList>
            </>
          )}
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="check" />} />
            <MdcListItemText primaryText={<PaymentPaid paid={reservation.is_paid_accommodation_tax} />} />
            <MdcListItemMeta meta={<small>宿泊税</small>} />
          </MdcListItem>
        </>
      )}

      {r.renderIf(!props.session.isOwner)(
        <AccommodationTax
          reservation_id={reservation.id}
          hotel_id={reservation.hotel_id}
          plan_code={reservation.plan_code}
          accommodation_tax={tax}
          booking_price={reservation.accommodation_fee}
          people={supposePeople(reservation)}
          stay={calcStayingDays(reservation.check_in, reservation.check_out)}
          site_referer={reservation.site_referer}
          plan_code={reservation.plan_code}
          onSave={v => onSave(v)}
          onDelete={accom_tax => AccommodationTaxAction.onRequestDelete(reservation.id, accom_tax.id)}
          onOpened={() => props.toggleFooter(false)}
          onClosed={() => props.toggleFooter(true)}
        >
          宿泊税を受領
        </AccommodationTax>
      )}

      <MdcListDivider tag="div" />
      <ListTitle title={'流入元'} />

      <MdcList nonInteractive>
        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="money" />} />
          <MdcListItemText primaryText={<Money>{reservation.commission}</Money>} />
          <MdcListItemMeta meta={<small>OTA手数料</small>} />
        </MdcListItem>

        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="category" />} />
          <MdcListItemText primaryText={reservation.site_referer} />
          <MdcListItemMeta meta={<small>流入元</small>} />
        </MdcListItem>

        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="code" />} />
          <MdcListItemText primaryText={reservation.reservation_code} />
          <MdcListItemMeta meta={<small>予約番号</small>} />
        </MdcListItem>

        <MdcListItem>
          <MdcListItemGraphic graphic={<MaterialIcon icon="web" />} />
          <MdcListItemText primaryText={reservation.channel_manager} />
          <MdcListItemMeta meta={<small>チャネルマネージャー</small>} />
        </MdcListItem>

        {r.renderIf(reservation.bookid)(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="pageview" />} />
            <MdcListItemText primaryText={Beds24Payment(reservation)} />
            <MdcListItemMeta meta={<small>決済URL</small>} />
          </MdcListItem>
        )}
      </MdcList>

      <MdcListDivider tag="div" />
      <ListTitle title={'その他'} />

      <MdcList nonInteractive>
        {r.renderIf(reservation.arrived_at)(
          <MdcListItem>
            <MdcListItemGraphic graphic={<MaterialIcon icon="access_time" />} />
            <MdcListItemText primaryText={dateToString(reservation.arrived_at)} />
            <MdcListItemMeta meta={<small>チェックイン時刻</small>} />
          </MdcListItem>
        )}

        {r.renderIf(reservation.guest_comment)(
          <MdcListItem style={{ height: 'auto', minHeight: '48px' }}>
            <MdcListItemGraphic graphic={<MaterialIcon icon="chat_bubble_outline" />} />
            <MdcListItemText primaryText={<React.Fragment>{nl2br(reservation.guest_comment)}</React.Fragment>} />
            <MdcListItemMeta meta={<small>ゲストコメント</small>} />
          </MdcListItem>
        )}

        {r.renderIf(reservation.rate_description)(
          <MdcListItem style={{ height: 'auto', minHeight: '48px' }}>
            <MdcListItemGraphic graphic={<MaterialIcon icon="speaker_notes" />} />
            <MdcListItemText className={`multiLine`} primaryText={LargeOutput(reservation.rate_description)} />
            <MdcListItemMeta meta={<small>レート</small>} />
          </MdcListItem>
        )}

        {r.renderIf(reservation.api_message)(
          <MdcListItem style={{ height: 'auto', minHeight: '48px' }}>
            <MdcListItemGraphic graphic={<MaterialIcon icon="speaker_notes" />} />
            <MdcListItemText className={`multiLine`} primaryText={LargeOutput(reservation.api_message)} />
            <MdcListItemMeta meta={<small>API</small>} />
          </MdcListItem>
        )}
      </MdcList>

      {/* CSSで高さが取れず表示が途中で切れてしまうので余白を入れている */}
      <br />
      <br />
      <br />
      <br />
    </MdcListGroup>
  )
}

function ListTitle(props) {
  return (
    <div style={{ marginLeft: '16px', marginTop: '16px', color: 'rgba(0, 0, 0, 0.38)' }}>
      <p>{props.title}</p>
    </div>
  )
}

function Beds24Payment(reservation) {
  if (!reservation.bookid) {
    return ''
  }
  return (
    <a href={`https://www.beds24.com/control2.php?ajax=bookedit&id=${reservation.bookid}&tab=1`} target="_blank">
      {reservation.bookid}
    </a>
  )
}

function LargeOutput(value) {
  if (!value) {
    return NoData()
  }

  return <small>{nl2br(value)}</small>
}

function NoData() {
  return <small>(記載なし)</small>
}

function supposePeople(reservation) {
  return reservation.num_adult + reservation.num_child
}

function valueOf(object, propertyName) {
  if (!object || !object.hasOwnProperty(propertyName)) {
    return ''
  }

  return object[propertyName]
}

function numberOf(object, propertyName) {
  if (!object || !object.hasOwnProperty(propertyName)) {
    return 0
  }

  return object[propertyName]
}

// function TaskList(props) {
//   const tasks = props.children.tasks
//   if (!tasks) {
//     return null
//   }
//
//   return (
//     <ul className="reservationTasks">
//       {tasks.map(task => (
//         <Task key={`task_${task.id}`}>{task}</Task>
//       ))}
//     </ul>
//   )
// }

// function Task(props) {
//   const task = props.children
//
//   return (
//     <li className="task my-1">
//       <div>
//         <input type="checkbox" name="status" value="closed" checked={task.status === 'closed'} onChange={e => TaskAction.onRequestUpdateStatus(e.target.checked, task)}/>
//         <span onClick={e => TaskAction.showDetail(task.reservation_id, task.id)}>{task.title}</span>
//         {r.renderIf(task.description)(<Icon type="description"/>)}
//       </div>
//     </li>
//   )
// }
